/**
 * AI 보고서 — 만든 것을 다시 보는 화면
 * ---------------------------------------------------------------------------
 * ## 왜 화면으로 뺐나
 * 보관함은 원래 자사 현황의 [AI 주간 보고서] **팝업 안 탭**으로만 있었다.
 * 그래서 보고서를 만든 적 없는 사람은 그런 것이 있는 줄도 몰랐고, 만든
 * 사람도 "아까 그 글" 을 어디서 다시 보는지 찾지 못했다. 팝업은 만드는
 * 자리고, 쌓인 것을 보는 자리는 메뉴에 있어야 한다. WORK&ECO 가 먼저
 * 그렇게 했고(AI 보고서 메뉴), 여기도 같은 모양으로 맞춘다.
 *
 * ## 다시 보는 데는 요금이 들지 않는다
 * 만들 때만 건당 요금이 붙는다. 그래서 만든 것을 남긴다 — AI 라 같은
 * 기간으로 다시 눌러도 글이 달라져서, 보고에 쓴 그 글은 여기서만 되찾을
 * 수 있다.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { App, Button, Card, Empty, Space, Spin, Table, Typography } from 'antd';
import { apiClient, type NormalizedError } from '../../lib/api-client';
import { errorText } from '../../lib/queries';
import { hasPermission, useAuth } from '../../lib/auth-store';
import { PageHelp } from '../../components/PageHelp';
import { GUIDE } from '../../lib/guide';
import { AiWeeklyReportButton } from './AiWeeklyReportModal';

const { Paragraph, Text } = Typography;

export interface SavedReport {
  aiReportId: string;
  periodStart: string;
  periodEnd: string;
  providerName: string | null;
  pricePerReport: number | null;
  createdAt: string;
  preview: string;
  consented: boolean;
  consentText: string | null;
}

export default function AiReportArchivePage() {
  const { message, modal } = App.useApp();
  const client = useQueryClient();
  const { profile } = useAuth();
  const canDelete = hasPermission(profile, 'platform.tenant:manage');
  const [openId, setOpenId] = useState<string | null>(null);

  const list = useQuery<SavedReport[], NormalizedError>({
    queryKey: ['ai-report', 'list'],
    queryFn: async () => (await apiClient.get<SavedReport[]>('/ai-report')).data,
  });

  const detail = useQuery<SavedReport & { body: string }, NormalizedError>({
    queryKey: ['ai-report', 'detail', openId],
    queryFn: async () =>
      (await apiClient.get<SavedReport & { body: string }>(`/ai-report/${openId}`)).data,
    enabled: Boolean(openId),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => apiClient.delete(`/ai-report/${id}`),
    onSuccess: async () => {
      message.success('보고서를 지웠습니다.');
      setOpenId(null);
      await client.invalidateQueries({ queryKey: ['ai-report', 'list'] });
    },
    onError: (err: NormalizedError) => message.error(errorText(err)),
  });

  const rows = list.data ?? [];

  /* ---- 한 건 펼쳐 보기 ---- */
  if (openId) {
    return (
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Card
          size="small"
          title="AI 보고서"
          loading={detail.isLoading}
          extra={
            <Space>
              <Button size="small" onClick={() => setOpenId(null)}>
                ← 목록
              </Button>
              {canDelete && detail.data && (
                <Button
                  size="small"
                  danger
                  loading={remove.isPending}
                  onClick={() =>
                    modal.confirm({
                      title: '이 보고서를 지울까요?',
                      content: '지우면 되돌릴 수 없습니다. 다시 만들려면 요금이 또 붙습니다.',
                      okText: '지우기',
                      okButtonProps: { danger: true },
                      cancelText: '그만두기',
                      onOk: () => remove.mutateAsync(openId).then(() => undefined),
                    })
                  }
                >
                  지우기
                </Button>
              )}
            </Space>
          }
        >
          {detail.isError && <Text type="danger">{errorText(detail.error)}</Text>}
          {detail.data && (
            <Space direction="vertical" size={10} style={{ width: '100%' }}>
              {/* 엔진·금액은 적지 않는다 — 기간과 만든 때만 */}
              <Text type="secondary">
                {detail.data.periodStart} ~ {detail.data.periodEnd} ·{' '}
                {new Date(detail.data.createdAt).toLocaleString('ko-KR')} 생성
              </Text>
              {/*
                동의 기록은 화면에 띄우지 않는다 — 만들 때 받았으니 읽는
                사람에게는 지난 일이고, 보고서 머리에 동의 문구가 붙으면
                정작 봐야 할 기간이 묻힌다. 기록은 tenant.ai_report 에
                그대로 남는다(consented · consent_text).
              */}
              <Paragraph
                style={{
                  whiteSpace: 'pre-wrap',
                  background: '#fafafa',
                  border: '1px solid #f0f0f0',
                  borderRadius: 8,
                  padding: 16,
                  margin: 0,
                }}
              >
                {detail.data.body}
              </Paragraph>
            </Space>
          )}
        </Card>
      </Space>
    );
  }

  /* ---- 목록 ---- */
  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <PageHelp title="AI 보고서 — 화면 설명">{GUIDE['ai-reports']?.body}</PageHelp>

      <Card
        size="small"
        title="AI 보고서"
        loading={list.isLoading}
        extra={<AiWeeklyReportButton />}
      >
        {list.isError && <Text type="danger">{errorText(list.error)}</Text>}
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          <Text type="secondary" style={{ fontSize: 13 }}>
            만드신 보고서가 모두 남아 있습니다. <b>다시 보는 데는 요금이 들지 않습니다.</b>
          </Text>
          {list.isLoading ? (
            <Spin />
          ) : rows.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="아직 만드신 보고서가 없습니다"
            />
          ) : (
            <Table<SavedReport>
              size="small"
              rowKey="aiReportId"
              dataSource={rows}
              pagination={{ pageSize: 15, hideOnSinglePage: true }}
              onRow={(r) => ({
                style: { cursor: 'pointer' },
                onClick: () => setOpenId(r.aiReportId),
              })}
              columns={[
                {
                  title: '분석 기간',
                  width: 200,
                  render: (_: unknown, r: SavedReport) => `${r.periodStart} ~ ${r.periodEnd}`,
                },
                {
                  title: '내용',
                  dataIndex: 'preview',
                  ellipsis: true,
                  render: (v: string) => (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {v || '(내용 없음)'}
                    </Text>
                  ),
                },

                /*
                 * 목록에는 금액을 적지 않는다 — 무료분으로 만든 것과 초과분으로
                 * 만든 것이 섞여 있어, 세로로 더하면 청구서와 맞지 않는다.
                 * 그때의 단가는 보고서를 열면 보인다. (WORK&ECO 와 같은 규칙)
                 */
                {
                  title: '만든 날짜',
                  dataIndex: 'createdAt',
                  width: 170,
                  render: (v: string) => new Date(v).toLocaleString('ko-KR'),
                },
              ]}
            />
          )}
        </Space>
      </Card>
    </Space>
  );
}
